/* eslint-disable camelcase */
/// List of all universal storage keys related to registration.
import date from '@/components/kit/date'
import { convertKeysToSnakeCaseIfAny } from '@/plugins/helpers'

const stateKeys = [
  'serial',
  'material',
  'source',
  'search-installation',
  'search-owner',
  'installationHasSameAddress',
  'scan-units-start.indoor-type',
  'scan-units-start.indoor-type-image',
  'scan-units.scanned',
  'scan-units.existing',
  'scan-units.code',
  'scan-units.installation',
  'scan-units.product-range',
  'scan-units.user-picked-product-range-id',
  'scan-units.unit-filter',
  'scan-units.unit-type-hint',
  'scan-units.multiple-product-ranges',
  'scan-units.gateway-device-id',
  'building.date',
  'commissioning.date',
  'building.house.date',
  'service-partner',
  'commissioning-code',
  'commissioning-code-invitation-response',
  'search-selection',
  'registration.flow-tracking.start-time',
  'registration.flow-tracking.visited-steps'
]

export const state = () => ({
  customerInfo: null,
  validRegistrableInstallations: [],
  unregistrableInstallations: [],
  currentStep: null,
  flowType: 'newInstallation', // newInstallation || existingInstallation || existingInstallationFromBanner || commissioningCode || commissioningCodeFromIndex
  flowConfigs: {
    newInstallation: [
      {
        name: 'overview',
        skipCondition: () => false,
        nextScreen: () => 'registration-start'
      },
      {
        name: 'index',
        skipCondition: () => false,
        nextScreen: () => 'registration-scan-units-start'
      },
      {
        name: 'registration-start',
        skipCondition: () => false,
        nextScreen: () => 'registration-scan-units-start'
      },
      {
        name: 'registration-scan-units-start',
        skipCondition: () => false,
        nextScreen: () => 'registration-scan-units-scan-code'
      },
      {
        name: 'registration-scan-units-scan-code',
        skipCondition: () => false,
        nextScreen: () => 'registration-scan-units-overview'
      },
      {
        name: 'registration-scan-units-overview',
        skipCondition: () => false,
        nextScreen: () => 'registration-commissioning-overview'
      },
      {
        name: 'registration-commissioning-overview',
        skipCondition: (data) => data.commissioning_date,
        nextScreen: () => 'registration-building-house'
      },
      {
        name: 'registration-building-house',
        skipCondition: (data) => {
          return !data.customer.require_building_finish_date
        },
        nextScreen: () => 'registration-end-user-personal-info'
      },
      {
        name: 'registration-end-user-personal-info',
        skipCondition: (data) => {
          return (
            data?.first_name &&
            data?.last_name &&
            data?.email &&
            data?.phone_number
          )
        },
        nextScreen: () => 'registration-end-user-address'
      },
      {
        name: 'registration-end-user-address',
        skipCondition: (data) => {
          return (
            data?.street &&
            data?.number &&
            data?.postal_code &&
            data?.city &&
            data?.country
          )
        },
        nextScreen: () => 'registration-scan-units-installation-address'
      },
      {
        name: 'registration-scan-units-installation-address',
        skipCondition: (data) =>
          data?.street &&
          data?.number &&
          data?.postal_code &&
          data?.city &&
          data?.country,
        nextScreen: () => 'registration-service-partner-overview'
      },
      {
        name: 'registration-service-partner-overview',
        skipCondition: (data) => {
          return data?.company_name
        },
        nextScreen: () => 'registration-summary-overview'
      },
      {
        name: 'registration-summary-overview',
        skipCondition: () => {
          return false
        },
        nextScreen: () => null
      }
    ],
    existingInstallation: [
      {
        name: 'index',
        skipCondition: () => false,
        nextScreen: () => 'registration-end-user-personal-info'
      },
      {
        name: 'overview',
        skipCondition: () => true,
        nextScreen: () => 'registration-start'
      },
      {
        name: 'registration-start',
        skipCondition: () => false,
        nextScreen: () => 'registration-end-user-personal-info'
      },
      {
        name: 'registration-end-user-personal-info',
        skipCondition: (data) => {
          return (
            data?.first_name &&
            data?.last_name &&
            data?.email &&
            data?.phone_number
          )
        },
        nextScreen: () => 'registration-end-user-address'
      },
      {
        name: 'registration-end-user-address',
        skipCondition: (data) => {
          return (
            data?.street &&
            data?.number &&
            data?.postal_code &&
            data?.city &&
            data?.country
          )
        },
        nextScreen: () => 'registration-installations-overview'
      },
      {
        name: 'registration-installations-overview',
        skipCondition: (data) => data?.length === 1,
        nextScreen: () => 'registration-scan-units-overview'
      },
      {
        name: 'registration-scan-units-overview',
        skipCondition: () => false,
        nextScreen: () => 'registration-commissioning-overview'
      },
      {
        name: 'registration-commissioning-overview',
        skipCondition: (data) => data.commissioning_date,
        nextScreen: () => 'registration-building-house'
      },
      {
        name: 'registration-building-house',
        skipCondition: (data) => {
          return !data.customer.require_building_finish_date
        },
        nextScreen: () => 'registration-scan-units-installation-address'
      },
      {
        name: 'registration-scan-units-installation-address',
        skipCondition: (data) =>
          data?.street &&
          data?.number &&
          data?.postal_code &&
          data?.city &&
          data?.country,
        nextScreen: () => 'registration-service-partner-overview'
      },
      {
        name: 'registration-service-partner-overview',
        skipCondition: (data) => {
          return data?.company_name
        },
        nextScreen: () => 'registration-summary-overview'
      },
      {
        name: 'registration-summary-overview',
        skipCondition: () => {
          return false
        },
        nextScreen: () => null
      }
    ],
    existingInstallationFromBanner: [
      {
        name: 'overview',
        skipCondition: () => true,
        nextScreen: () => 'registration-installations-overview'
      },
      {
        name: 'registration-installations-overview',
        skipCondition: (data) => {
          return data?.length === 1
        },
        nextScreen: () => 'registration-scan-units-overview'
      },
      {
        name: 'registration-scan-units-overview',
        skipCondition: () => false,
        nextScreen: () => 'registration-commissioning-overview'
      },
      {
        name: 'registration-commissioning-overview',
        skipCondition: (data) => data.commissioning_date,
        nextScreen: () => 'registration-building-house'
      },
      {
        name: 'registration-building-house',
        skipCondition: (data) => {
          return !data.customer.require_building_finish_date
        },
        nextScreen: () => 'registration-scan-units-installation-address'
      },
      {
        name: 'registration-scan-units-installation-address',
        skipCondition: (data) =>
          data?.street &&
          data?.number &&
          data?.postal_code &&
          data?.city &&
          data?.country,
        nextScreen: () => 'registration-service-partner-overview'
      },
      {
        name: 'registration-service-partner-overview',
        skipCondition: (data) => {
          return data?.company_name
        },
        nextScreen: () => 'registration-end-user-personal-info'
      },
      {
        name: 'registration-end-user-personal-info',
        skipCondition: (data) => {
          return (
            data?.first_name &&
            data?.last_name &&
            data?.email &&
            data?.phone_number
          )
        },
        nextScreen: () => 'registration-end-user-address'
      },
      {
        name: 'registration-end-user-address',
        skipCondition: (data) => {
          return (
            data?.street &&
            data?.number &&
            data?.postal_code &&
            data?.city &&
            data?.country
          )
        },
        nextScreen: () => 'registration-summary-overview'
      },
      {
        name: 'registration-summary-overview',
        skipCondition: () => {
          return false
        },
        nextScreen: () => null
      }
    ],
    commissioningCode: [
      {
        name: 'overview',
        skipCondition: () => false,
        nextScreen: () => 'registration-start'
      },
      {
        name: 'registration-start',
        skipCondition: () => false,
        nextScreen: () => 'registration-scan-units-commissioning-code'
      },
      {
        name: 'registration-scan-units-overview',
        skipCondition: () => false,
        nextScreen: () => 'registration-building-house'
      },
      {
        name: 'registration-scan-units-commissioning-code',
        skipCondition: () => false,
        nextScreen: () => 'registration-scan-units-overview'
      },
      {
        name: 'registration-building-house',
        skipCondition: (data) => {
          return !data.customer.require_building_finish_date
        },
        nextScreen: () => 'registration-summary-overview'
      },
      {
        name: 'registration-summary-overview',
        skipCondition: () => {
          return false
        },
        nextScreen: () => null
      }
    ],
    commissioningCodeFromIndex: [
      {
        name: 'index',
        skipCondition: () => false,
        nextScreen: () => 'registration-scan-units-commissioning-code'
      },
      {
        name: 'registration-scan-units-overview',
        skipCondition: () => false,
        nextScreen: () => 'registration-building-house'
      },
      {
        name: 'registration-scan-units-commissioning-code',
        skipCondition: () => false,
        nextScreen: () => 'registration-scan-units-overview'
      },
      {
        name: 'registration-building-house',
        skipCondition: (data) => {
          return !data.customer.require_building_finish_date
        },
        nextScreen: () => 'registration-summary-overview'
      },
      {
        name: 'registration-summary-overview',
        skipCondition: () => {
          return false
        },
        nextScreen: () => null
      }
    ]
  },
  navigationHistory: []
})

export const mutations = {
  pushToHistory(state, screen) {
    state.navigationHistory.push(screen)
  },
  popFromHistory(state) {
    state.navigationHistory.pop()
  },
  setCustomerInfo(state, value) {
    state.customerInfo = value
  },
  setValidRegistrableInstallations(state, installations) {
    state.validRegistrableInstallations = installations
  },
  setUnregistrableInstallations(state, installations) {
    state.unregistrableInstallations = installations
  },
  setFlow(state, value) {
    state.flowType = value
  },
  setCurrentScreen(state, value) {
    state.currentScreen = value
  }
}

export const actions = {
  /// Clears any registration state
  clearRegistration({ commit, dispatch }) {
    for (let ix = 0; ix < stateKeys.length; ix++) {
      this.$storage.removeUniversal(stateKeys[ix])
    }
    commit('setCurrentScreen', 'overview')
    dispatch('disposeFlowTracking')
  },
  initializeFlowTracking() {
    this.$storage.setUniversal(
      'registration.flow-tracking.start-time',
      Date.now()
    )
    this.$storage.setUniversal('registration.flow-tracking.visited-steps', [])
  },
  trackStep(_, stepName) {
    if (stepName) {
      const visitedStepsArray =
        this.$storage.getUniversal(
          'registration.flow-tracking.visited-steps'
        ) || []
      const visitedSteps = new Set(visitedStepsArray)
      visitedSteps.add(stepName)
      this.$storage.setUniversal(
        'registration.flow-tracking.visited-steps',
        Array.from(visitedSteps)
      )
    }
  },
  disposeFlowTracking() {
    this.$storage.removeUniversal('registration.flow-tracking.start-time')
    this.$storage.removeUniversal('registration.flow-tracking.visited-steps')
  },
  async getNextScreen({ state, commit }) {
    const { currentScreen, flowType } = state
    const flow = state.flowConfigs[flowType]
    const userInfo = await this.$storage.getUniversal('end-user.personal-info')
    const customerInfo = await this.$storage.getUniversal('customer-info')
    const userAddress = await this.$storage.getUniversal('end-user.address')

    const storedInstallation = await this.$storage.getUniversal(
      'scan-units.installation'
    ) // Installation object updating with user input
    const installation =
      (await customerInfo?.installations.find(
        (installation) =>
          installation.localInstallationId ===
          storedInstallation?.localInstallationId
      )) || {} // Installation received from the API

    const servicePartner = installation?.service_partner || {}

    const validInstallations = await this.$storage.getUniversal(
      'registration.valid-installations'
    )
    const commissioningCode = await this.$storage.getUniversal(
      'commissioning-code'
    )
    const fallbackScreen = currentScreen || flow[0]?.name
    if (!currentScreen) {
      commit('pushToHistory', fallbackScreen)
    }
    let nextScreenArgs =
      currentScreen === 'registration-installations-overview'
        ? validInstallations
        : commissioningCode
    let next = flow
      .find((screen) => screen.name === fallbackScreen)
      ?.nextScreen(nextScreenArgs)
    while (next) {
      const nextScreenConfig = flow.find((screen) => screen.name === next)
      if (!nextScreenConfig) return null
      let data
      switch (nextScreenConfig.name) {
        case 'registration-installations-overview': {
          if (
            flowType === 'commissioningCode' ||
            flowType === 'commissioningCodeFromIndex'
          ) {
            const commissionedInstallations = validInstallations.filter(
              (installation) =>
                installation.status === 'commissioned' && installation.id
            )
            data = commissionedInstallations
            nextScreenArgs = commissionedInstallations
            break
          }
          data = validInstallations
          nextScreenArgs = validInstallations

          break
        }
        case 'registration-building-house':
          data = { customer: customerInfo?.customer, installation }
          break
        case 'registration-scan-units-start':
        case 'registration-building-installation':
        case 'registration-commissioning-overview':
          data = installation
          break
        case 'registration-end-user-personal-info':
          data = userInfo
          break
        case 'registration-end-user-address':
          data = userAddress.customer_details.address
          break
        case 'registration-service-partner-overview':
          data = servicePartner
          break
        case 'registration-scan-units-installation-address':
          data = installation?.address
          break
      }

      if (!nextScreenConfig.skipCondition(data)) {
        commit('setCurrentScreen', next)
        if (currentScreen) commit('pushToHistory', currentScreen)
        return next
      }

      next = nextScreenConfig.nextScreen(nextScreenArgs)
    }

    return null
  },
  getPreviousScreen({ state, commit }) {
    if (state.navigationHistory.length === 0) {
      return null
    }
    const previousScreen =
      state.navigationHistory[state.navigationHistory.length - 1]

    commit('setCurrentScreen', previousScreen)
    commit('popFromHistory') // Remove last entry from history
    return previousScreen
  },
  clearUnitInputState() {
    this.$storage.removeUniversal('search-selection')
    this.$storage.removeUniversal('serial')
    this.$storage.removeUniversal('material')
    this.$storage.removeUniversal('source')
  },
  clearCommissioningCodeInvitationState() {
    this.$storage.removeUniversal('commissioning-code-invitation-response')
  },
  setUnitInputFilter(_, unit) {
    this.$storage.setUniversal('scan-units.unit-type-hint', unit.type)

    // Check first if we have still multiple product ranges & the user did not pick one
    const multiple = this.$storage.getUniversal(
      'scan-units.multiple-product-ranges'
    )
    const pickedProductRangeId = this.$storage.getUniversal(
      'scan-units.user-picked-product-range-id'
    )
    if (multiple && multiple.length > 1 && !pickedProductRangeId) {
      // We have multiple product ranges & the user did not picked one
      // Retrieve all the possible unit types with their possible material codes from all product ranges
      const allInstallationUnits = multiple.map(
        (elem, index) => elem.installation_units
      )
      // Merge it to one array
      let allUnitTypeTemplates = []
      for (let i = 0; i < allInstallationUnits?.length; i++) {
        allUnitTypeTemplates = allUnitTypeTemplates.concat(
          allInstallationUnits[i]
        )
      }
      // Filter out the necessary type
      const requiredUnitTypeTemplates = allUnitTypeTemplates.filter((u) => {
        return u.type === unit.type
      })
      if (requiredUnitTypeTemplates.length === 0) {
        // incorrect behaviour, the unit type is not present in the product range this should not happen
        console.error(
          `Attempt to filter for unit type ${unit.type} which is unknown to the multiple product ranges. Only known unit types can be added.`
        )
        this.$storage.removeUniversal('scan-units.unit-filter')
        return
      }

      // Merge all the material codes of the necessary unit type into one array
      let materialCodes = []
      for (let i = 0; i < requiredUnitTypeTemplates.length; i++) {
        materialCodes = materialCodes.concat(
          requiredUnitTypeTemplates[i].material_codes
        )
      }
      let materials = []
      materials = materialCodes.map((elem) => {
        return {
          material_code: elem
        }
      })
      this.$storage.setUniversal('scan-units.unit-filter', materials)
    } else {
      // We have no multiple product ranges, or the user already picked one
      const productRange = this.$storage.getUniversal(
        'scan-units.product-range'
      )
      if (!productRange) {
        // can't set a filter without a product range
        this.$storage.removeUniversal('scan-units.unit-filter')
        return
      }

      const unitTypeTemplate = productRange.installation_units.filter(
        (u) => u.type === unit.type
      )[0]

      if (!unitTypeTemplate) {
        // incorrect behaviour, the unit type is not present in the product range this should not happen
        console.error(
          `Attempt to filter for unit type ${unit.type} which is unknown to the current product range. Only known unit types can be added.`
        )
        this.$storage.removeUniversal('scan-units.unit-filter')
        return
      }

      const materials = unitTypeTemplate.material_codes.map((elem) => {
        return {
          material_code: elem
        }
      })
      this.$storage.setUniversal('scan-units.unit-filter', materials)
    }
  },
  setUnitType(_, { unitType, unitImage }) {
    this.$storage.setUniversal('scan-units-start.indoor-type', unitType)
    this.$storage.setUniversal('scan-units-start.indoor-type-image', unitImage)
  },
  addScannedUnit(_, unit) {
    // get the list of scanned units
    let units = this.$storage.getUniversal('scan-units.scanned') || []

    // ensure we don't duplicate a material + serial combination
    units = units.filter(
      (u) => !(u.material === unit?.material && u.serial === unit?.serial)
    )

    if (unit?.registered_on_sbm !== undefined) {
      units.push({
        material: unit?.material,
        serial: unit?.serial,
        type: unit?.type,
        mac_address: unit?.mac_address,
        registered_on_sbm: unit?.registered_on_sbm
      })
    } else {
      units.push({
        material: unit?.material,
        serial: unit?.serial,
        type: unit?.type,
        mac_address: unit?.mac_address
      })
    }
    // update the list of scanned units
    this.$storage.setUniversal('scan-units.scanned', units)
  },
  /// Returns a list of units which need to be appended to validation requests.
  /// Such units include units provided by the user so far, excluding units present in installation details.
  getUnitsToValidate() {
    const scannedUnits = this.$storage.getUniversal('scan-units.scanned') || []
    const installation = this.$storage.getUniversal(
      'scan-units.installation'
    ) || { units: [] }
    return scannedUnits.filter((u) =>
      installation.units.filter(
        (iu) => !(iu.material === u.material && iu.serial === u.serial).length
      )
    )
  },
  /// Returns `true` if the given unit is already a member of a confirmed installation.
  getUnitIsAlreadyConfirmed(_, { material, serial }) {
    const installation = this.$storage.getUniversal(
      'scan-units.installation'
    ) || { units: [] }
    const match = installation.units.filter(
      (iu) => iu.material === material && iu.serial === serial
    )[0]
    if (match) {
      return true
    }
    return false
  },
  getScannedAndInstalledUnits() {
    // get the list of scanned units
    const scannedUnits = this.$storage.getUniversal('scan-units.scanned') || []
    const installation = this.$storage.getUniversal(
      'scan-units.installation'
    ) || { units: [], product_range: {} }
    const scannedAndInstalledUnits = []
    // we first add the units from the installation so the readonly flag can be set
    if (installation?.units?.length) {
      for (const unit of installation?.units) {
        if (
          !scannedAndInstalledUnits.filter(
            (u) => u.material === unit.material && u.serial === unit.serial
          ).length
        ) {
          scannedAndInstalledUnits.push({ ...unit, readOnly: true })
        }
      }
    }

    if (scannedUnits?.length) {
      for (const unit of scannedUnits) {
        // only add new units
        if (
          !scannedAndInstalledUnits.filter(
            (u) => u.material === unit.material && u.serial === unit.serial
          ).length
        ) {
          scannedAndInstalledUnits.push({ ...unit })
        }
      }
    }
    return scannedAndInstalledUnits || []
  },
  /// Returns a collection of units + unit slots for the registration overview.
  /// This includes already scanned units, units from a recognized installation + slots for extra units from the product range.
  /// This method is responsible for recalculating and storing the current product range.
  /// We do this every time because given changes to the scanned units we must provide an adapted result.
  async getUnitsForOverview({ dispatch }, preferredProductRangeId) {
    const scannedAndInstalledUnits = await dispatch(
      'getScannedAndInstalledUnits'
    )
    if (!scannedAndInstalledUnits || !scannedAndInstalledUnits?.length) {
      // to prevent an error on $marlonApi.productRangeSearch
      return []
    }
    const country =
      this.$storage.getUniversal('gigyaProfile') &&
      this.$storage.getUniversal('gigyaProfile').country
    const language =
      this.$storage.getUniversal('language') &&
      this.$storage.getUniversal('language').substr(0, 2)
    const searchResponse = await this.$marlonApi.productRangeSearch(
      scannedAndInstalledUnits,
      country,
      language
    )

    if (!searchResponse) {
      // API call failed
      return []
    }

    // choose a product range, take into account the requested preference, if possible
    let productRange = searchResponse?.product_ranges?.filter(
      (p) => p.id === preferredProductRangeId
    )[0]

    if (!productRange) {
      // if we've previously selected a product range, try to use it again
      const alreadySelected = this.$storage.getUniversal(
        'scan-units.product-range'
      )
      if (alreadySelected) {
        productRange = searchResponse.product_ranges?.filter(
          (p) => p.id === alreadySelected.id
        )[0]
      }
    }

    // if the search results are narrowed down to 1 product range, then use that one
    if (!productRange && searchResponse.product_ranges?.length === 1) {
      productRange = searchResponse.product_ranges[0]
    }

    if (!searchResponse.product_ranges?.length) {
      // no or unknown product range, we only show the installed units
      const unitReadOnly = scannedAndInstalledUnits
        .filter((u) => u.readOnly)
        .map((u) => {
          return {
            type: u.type,
            material: u.material,
            serial: u.macAddress || u.mac_address || u.serial,
            gateway_device_id: u.gatewayDeviceId || u.gateway_device_id,
            mac_address: u.macAddress || u.mac_address,
            required: true,
            minimNumber: 1,
            maximumNumber: 1
          }
        })
      return unitReadOnly || []
    }
    this.$storage.setUniversal(
      'scan-units.multiple-product-ranges',
      searchResponse.product_ranges
    )

    // update the product range we are using for this installation
    if (productRange) {
      this.$storage.setUniversal('scan-units.product-range', productRange)
    }
    // get all the units that we can use to show in the overview
    let allInstallationUnits = []
    if (productRange) {
      allInstallationUnits = productRange.installation_units
    } else {
      // merge multiple arrays in one
      for (const myProductRange of searchResponse.product_ranges) {
        for (const installationUnits of myProductRange.installation_units) {
          const installationUnitType = allInstallationUnits.find(
            (iu) => iu.type === installationUnits.type
          )
          if (installationUnitType) {
            allInstallationUnits.map((x) => {
              if (x.type === installationUnits.type) {
                return {
                  ...x,
                  material_codes: x.material_codes
                    .concat(installationUnits.material_codes)
                    .filter(
                      (value, index, self) => self.indexOf(value) === index
                    ),
                  maximum_number: Math.max(
                    x.maximum_number,
                    installationUnits.maximum_number
                  ),
                  required: x.required || installationUnits.required
                }
              }
            })
          } else {
            allInstallationUnits.push(installationUnits)
          }
        }
      }
    }

    // apply unit types from the product ranges.
    // units coming from existing installations already have their type set
    for (let i = 0; i < scannedAndInstalledUnits.length; i++) {
      const unit = scannedAndInstalledUnits[i]
      if (!unit.type) {
        const matchingTemplate = allInstallationUnits.filter((t) =>
          t.material_codes.includes(unit.material)
        )[0]
        if (matchingTemplate) {
          unit.type = matchingTemplate.type
        }
      }
    }

    const result = []
    for (const templateUnits of allInstallationUnits) {
      const scannedOrInstalledOfType = scannedAndInstalledUnits.filter(
        (u) => u.type === templateUnits.type
      )

      for (
        let typeIndex = 0;
        typeIndex < templateUnits.maximum_number;
        typeIndex++
      ) {
        const alreadyFilledUnit = scannedOrInstalledOfType[typeIndex]
        const unitOptional =
          !templateUnits.required ||
          (templateUnits.maximum_number > 1 && typeIndex > 0)
        if (alreadyFilledUnit) {
          result.push({
            type: templateUnits.type,
            material: alreadyFilledUnit.material,
            serial:
              alreadyFilledUnit.macAddress ||
              alreadyFilledUnit.mac_address ||
              alreadyFilledUnit.serial,
            gateway_device_id:
              alreadyFilledUnit.gatewayDeviceId ||
              alreadyFilledUnit.gateway_device_id,
            mac_address:
              alreadyFilledUnit.macAddress || alreadyFilledUnit.mac_address,
            required: !unitOptional,
            readOnly: alreadyFilledUnit.readOnly || false
          })
        } else {
          // empty slot for a unit type
          result.push({
            type: templateUnits.type,
            material: null,
            serial: null,
            gateway_device_id: null,
            mac_address: null,
            required: !unitOptional,
            readOnly: false,
            hintMaterialCodes: templateUnits.material_codes
          })
          // break early so we only reveal 1 extra slot for each type
          break
        }
      }
    }

    return result
  },
  setCommissioningDate(_, date) {
    const installation = this.$storage.getUniversal('scan-units.installation')
    const updatedInstallation = {
      ...installation,
      commissioning_date: date
    }
    this.$storage.setUniversal('scan-units.installation', updatedInstallation)
  },
  getCommissioningDate() {
    const installation = this.$storage.getUniversal('scan-units.installation')
    return installation?.commissioning_date
  },
  getCommissioningCode() {
    const installation = this.$storage.getUniversal('scan-units.installation')
    return installation?.commissioning_code
  },
  setBuildDate(_, date) {
    const installation = this.$storage.getUniversal('scan-units.installation')
    if (!installation) return
    const updatedInstallation = {
      ...installation,
      installation_date: date
    }
    this.$storage.setUniversal('scan-units.installation', updatedInstallation)
  },
  setHouseBuildDate(_, date) {
    this.$storage.setUniversal('building.house.date', date)
  },
  getBuildDate() {
    const installation = this.$storage.getUniversal('scan-units.installation')
    return installation.installation_date
  },
  setPersonalInfo(_, info) {
    this.$storage.setUniversal('end-user.personal-info', info)
  },
  getPersonalInfo() {
    return this.$storage.getUniversal('end-user.personal-info')
  },
  setAddress(_, info) {
    this.$storage.setUniversal('end-user.address', info)
  },
  getAddress() {
    return this.$storage.getUniversal('end-user.address')
  },
  storeInstallationAddress(_, address) {
    const installation = this.$storage.getUniversal('scan-units.installation')
    if (!installation) return
    const updatedInstallation = {
      ...installation,
      address
    }
    this.$storage.setUniversal('scan-units.installation', updatedInstallation)
  },
  setGatewayDeviceId(_, gatewayDeviceId) {
    this.$storage.setUniversal('scan-units.gateway-device-id', gatewayDeviceId)
  },
  getGatewayDeviceId() {
    return this.$storage.getUniversal('scan-units.gateway-device-id')
  },
  setServicePartner(_, servicePartner) {
    this.$storage.setUniversal('service-partner', servicePartner)
  },
  getServicePartner() {
    return this.$storage.getUniversal('service-partner')
  },
  setValidRegistrableInstallations({ dispatch }, installation) {
    this.$storage.setUniversal('scan-units.scanned', installation.units)
    if (installation.product_range) {
      this.$storage.setUniversal(
        'scan-units.product-range',
        installation.product_range
      )
    }
    if (installation.service_partner) {
      dispatch('setServicePartner', installation.service_partner)
    }
    this.$storage.setUniversal('scan-units.installation', installation)
  },
  removeValidRegistrableInstallation({ dispatch }) {
    this.$storage.removeUniversal('scan-units.scanned')
    this.$storage.removeUniversal('scan-units.installation')
    this.$storage.removeUniversal('scan-units.unit-filter')
    this.$storage.removeUniversal('scan-units.unit-type-hint')
    this.$storage.removeUniversal('scan-units.user-picked-product-range-id')

    this.$storage.removeUniversal('service-partner')
    dispatch('clearUnitInputState')
  },
  setCommissioningInvitationResponse(_, response) {
    this.$storage.setUniversal(
      'commissioning-code-invitation-response',
      response
    )
  },
  getCommissioningInvitationResponse() {
    return this.$storage.getUniversal('commissioning-code-invitation-response')
  },
  async registerInstallation({ dispatch, commit }) {
    try {
      const commissionedInstallation = this.$storage.getUniversal(
        'scan-units.installation'
      )
      const productRange = this.$storage.getUniversal(
        'scan-units.product-range'
      )
      const productRangeWithoutImage = { ...productRange }
      delete productRangeWithoutImage.image_url
      const allUnits = await dispatch('getUnitsForOverview', productRange.id)
      const endUserPersonalInfo = await dispatch('getPersonalInfo')
      const endUserAddress = await dispatch('getAddress')
      const houseBuildDate = this.$storage.getUniversal('building.house.date')
      const servicePartnerInfo = await dispatch('getServicePartner')
      const commissioningDateInfo = commissionedInstallation.commissioning_date
      const gatewayDeviceId = await dispatch('getGatewayDeviceId')
      const commissioningCode = commissionedInstallation.commissioning_code
      const commissioningDate =
        commissioningDateInfo || commissionedInstallation.commissioning_date
      const units = allUnits
        .filter((unit) => unit.material && unit.serial)
        .map((unit) => {
          return {
            type: unit.type,
            material: unit.material,
            serial: unit.mac_address || unit.macAddress || unit.serial,
            mac_address: unit.mac_address || unit.macAddress,
            gateway_device_id:
              unit.gatewayDeviceId || unit.gateway_device_id || null
          }
        })
      const installationAddress =
        commissionedInstallation?.address ||
        endUserAddress?.installation_address ||
        endUserAddress?.customer_details.address

      const request = {
        customer_details: {
          ...endUserPersonalInfo,
          ...endUserAddress.customer_details
        },
        installation_address: installationAddress,
        installer_id: servicePartnerInfo.id,
        installer_name: servicePartnerInfo.full_name,
        commissioning_date: date.toISODateString(commissioningDate),
        product_range_id: productRangeWithoutImage.id,
        commissioning_code: commissioningCode || null,
        units,
        gateway_device_id:
          gatewayDeviceId ||
          units.find((u) => u.type === 'lan_adapter')?.gatewayDeviceId
      }

      if (houseBuildDate) {
        request.build_date = date.toISODateString(houseBuildDate)
      }

      const response = await this.$marlonApi.registerInstallation(request)
      return response
    } catch (err) {
      this.$sentry.captureException(err)
      if (err.response?.status === 400) {
        return {
          error: true,
          status: -1,
          message: err.response?.data?.errors
        }
      } else {
        return {
          error: true,
          status: err.response?.status || 500,
          message: 'registration-failed'
        }
      }
    }
  },
  async addUnitToRegisteredInstallation(_, { installationId, unit }) {
    try {
      const response = await this.$marlonApi.addUnitToRegisteredInstallation(
        installationId,
        unit
      )
      return response
    } catch (err) {
      this.$sentry.captureException(err)
      throw err
    }
  },
  async searchMaterialCode(
    { rootState },
    { keyword, launcherStatusIsRegister }
  ) {
    // search from the service
    const country =
      (rootState.gigyaProfile && rootState.gigyaProfile.country) ||
      (this.$storage.getUniversal('gigyaProfile') &&
        this.$storage.getUniversal('gigyaProfile').country)
    const language =
      this.$storage.getUniversal('language') &&
      this.$storage.getUniversal('language').substr(0, 2)

    const materialResults = await this.$marlonApi.searchMaterialCode(
      keyword,
      country,
      language
    )
    const scannedUnits = this.$storage.getUniversal('scan-units.scanned')
    if (
      materialResults?.materialcodes &&
      !scannedUnits &&
      !launcherStatusIsRegister
    ) {
      materialResults.materialcodes = materialResults.materialcodes.filter(
        (m) => m.type !== 'lan_adapter'
      )
    }
    return materialResults
  },
  async getCustomerInfo({ commit, dispatch }) {
    dispatch('clearCustomerInfo')
    try {
      // Actual data
      const accessToken = await this.$storage.getUniversal('gigyaToken')
      const response = await this.$marlonApi.getCustomerInfo(accessToken)
      if (response && response.success) {
        const responseSnake = convertKeysToSnakeCaseIfAny(response) // ensure casing consistency for data coming from iO
        // Add a local installation id to each installation
        let localInstallationId = 1111
        if (responseSnake.installations?.length) {
          responseSnake.installations.forEach((installation) => {
            installation.localInstallationId = localInstallationId
            localInstallationId++
          })
        }
        commit('setCustomerInfo', responseSnake)
        this.$storage.setUniversal('customer-info', responseSnake)

        // Merge user info from account with the one from the customer info, prioritize the one from the account
        const { userInfo, userAddress } = await dispatch('mergeUserDetails')
        this.$storage.setUniversal('end-user.personal-info', userInfo)
        this.$storage.setUniversal('end-user.address', userAddress)

        return response
      }
    } catch (err) {
      this.$sentry.captureException(err)
      throw err
    }
  },
  clearCustomerInfo() {
    this.$storage.removeUniversal('customer-info')
    this.$storage.removeUniversal('end-user.personal-info')
    this.$storage.removeUniversal('end-user.address')
    this.$storage.removeUniversal('registration.valid-installations')
    this.$storage.removeUniversal('registration.unregistrable-installations')
    this.$storage.removeUniversal('registration.updatable-installations')
    this.$storage.removeUniversal('scan-units.installation')
    this.$storage.removeUniversal('scan-units.product-range')
    this.$storage.removeUniversal('scan-units.scanned')
    this.$storage.removeUniversal('service-partner')
    this.$storage.removeUniversal('scan-units.user-picked-product-range-id')
  },
  async classifyInstallations({ commit, dispatch }) {
    // Classify installations received from the API to registrable, unregistrable and updatable

    // Requried data
    const customerInfo = this.$storage.getUniversal('customer-info')
    const account = await this.$storage.getUniversal('account')
    if (
      !customerInfo ||
      !customerInfo?.installations ||
      !customerInfo?.installations.length
    )
      return
    const previouslyFailedRegistrations = await this.$storage.getUniversal(
      'registration.failed-registrations'
    )
    const ONE_WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000

    // Possible classifications
    const unregistrableInstallations = []
    const validRegistrableInstallations = []
    const updatableInstallations = []

    // Iterate over installations and classify them
    for (const inst of customerInfo?.installations) {
      const installation = JSON.parse(JSON.stringify(inst))
      const { status, customer_id, units } = installation

      // Check if installation is already registered and can be updated
      if (
        status === 'registered' &&
        customer_id === customerInfo?.customer.id
      ) {
        if (
          units.some(
            (unit) => unit.type === 'lan_adapter' && !unit.registered_on_sbm
          )
        ) {
          updatableInstallations.push(installation)
        }
        continue
      }

      // Check if the installation is in the same country as the user
      if (
        installation?.address?.country &&
        installation?.address?.country.toLowerCase() !==
          account?.country?.toLowerCase()
      ) {
        unregistrableInstallations.push(installation)
        continue
      }

      // Check if the installation belongs to another user
      if (customer_id && customer_id !== customerInfo?.customer.id) {
        unregistrableInstallations.push(installation)
        continue
      }

      // Handle installations that are not yet fully registered
      if (
        !status ||
        ['commissioned', 'incomplete', 'unlinked'].includes(status)
      ) {
        // Filter out units with incomplete data, keeping only units with complete data
        const completeUnits = units.filter((unit) => {
          if (unit.type === 'lan_adapter') {
            return unit.material && (unit.serial || unit.mac_address)
          } else {
            return unit.material && unit.serial
          }
        })

        // If no units have complete data, mark installation as unregistrable
        if (completeUnits.length === 0) {
          unregistrableInstallations.push(installation)
          continue
        }

        // Update installation with only complete units
        installation.units = completeUnits
        installation.completely_registered = !!customer_id?.length

        const installationIdentifier = await dispatch(
          'installation/generateInstallationIdentifier',
          completeUnits,
          { root: true }
        )

        // Check if the installation registration has failed before.
        // If so, check if the last failed attempt was more than a week ago
        // If not, add the installation to the list of unregistrable installations
        // If yes, remove the failed registration and add the installation to the list of valid registrable installations
        const failedRegistration = previouslyFailedRegistrations?.find(
          (attempt) => attempt.identifier === installationIdentifier
        )

        if (failedRegistration) {
          const timeSinceFailiure = Date.now() - failedRegistration.date
          if (timeSinceFailiure <= ONE_WEEK_IN_MS) {
            installation.registrationFailed = true
            unregistrableInstallations.push(installation)
          } else {
            dispatch('removeFailedRegistration', {
              failedRegistration,
              previouslyFailedRegistrations
            })
            validRegistrableInstallations.push(installation)
          }
        } else {
          validRegistrableInstallations.push(installation)
        }
        continue
      }
      // The installation has a status that is not registrable
      unregistrableInstallations.push(installation)
    }

    // Store classified installations
    if (validRegistrableInstallations.length) {
      this.$storage.setUniversal(
        'registration.valid-installations',
        validRegistrableInstallations
      )
      commit('setValidRegistrableInstallations', validRegistrableInstallations)
    }

    if (unregistrableInstallations.length) {
      commit('setUnregistrableInstallations', unregistrableInstallations)
      this.$storage.setUniversal(
        'registration.unregistrable-installations',
        unregistrableInstallations
      )
    }

    if (updatableInstallations.length) {
      this.$storage.setUniversal(
        'registration.updatable-installations',
        updatableInstallations
      )
    }
  },
  async clearUpdatableInstallations(_, installationId) {
    const updatableInstallations = this.$storage.getUniversal(
      'registration.updatable-installations'
    )
    if (!updatableInstallations || !updatableInstallations.length) return

    const updatedUpdatableInstallations = updatableInstallations.filter(
      (installation) => installation.id !== installationId
    )

    if (updatedUpdatableInstallations.length === 0) {
      this.$storage.removeUniversal('registration.updatable-installations')
      return
    }

    await this.$storage.setUniversal(
      'registration.updatable-installations',
      updatedUpdatableInstallations
    )
  },
  async verifyProductRanges({ dispatch }) {
    const validInstallations = this.$storage.getUniversal(
      'registration.valid-installations'
    )
    if (!validInstallations || !validInstallations.length) return

    const country = this.$storage.getUniversal('gigyaProfile')?.country
    const language = this.$storage.getUniversal('language')?.substr(0, 2)

    const updatedInstallations = []
    for (const installation of validInstallations) {
      const plainInstallation = JSON.parse(JSON.stringify(installation))

      let productRange = plainInstallation.product_range
      let multipleProductRanges = []

      if (!plainInstallation.product_range?.id) {
        const productRanges = await this.$marlonApi.productRangeSearch(
          installation.units,
          country,
          language
        )

        if (productRanges.success && productRanges.product_ranges.length) {
          if (productRanges.product_ranges.length === 1) {
            productRange = {
              ...plainInstallation.product_range,
              ...productRanges.product_ranges[0]
            }
          }
          multipleProductRanges = productRanges.product_ranges
        }
      }

      const updatedInstallation = {
        ...plainInstallation,
        product_range: productRange,
        multiple_product_ranges: multipleProductRanges
      }

      // Verify that there is at least one registrable product range
      const isRegistrableProductRange = updatedInstallation.product_range?.id
        ? !updatedInstallation.product_range?.unregistrable // TODO: temporary property name
        : updatedInstallation.multiple_product_ranges.some(
            (productRange) => !productRange.unregistrable // TODO: temporary property name
          )

      if (isRegistrableProductRange) {
        updatedInstallations.push(updatedInstallation)
      } else {
        dispatch('addUnregistrableInstallation', updatedInstallation)
      }
    }
    this.$storage.setUniversal(
      'registration.valid-installations',
      updatedInstallations
    )
  },
  async removeFailedRegistration(_, data) {
    const updatedFailedRegistrations = data.previouslyFailedRegistrations.filter(
      (attempt) => attempt.identifier !== data.failedRegistration.identifier
    )
    if (updatedFailedRegistrations.length === 0) {
      this.$storage.removeUniversal('registration.failed-registrations')
      return
    }
    await this.$storage.setUniversal(
      'registration.failed-registrations',
      updatedFailedRegistrations
    )
  },
  async mergeUserDetails() {
    const account = await this.$storage.getUniversal('account')
    const sbmUserDetail = await this.$storage.getUniversal('customer-info')
    const userInfo = {}
    const userAddress = {
      customer_details: {
        address: {}
      }
    }
    userInfo.first_name =
      account?.customer_details?.first_name || sbmUserDetail.customer.first_name
    userInfo.last_name =
      account?.customer_details?.last_name || sbmUserDetail.customer.last_name
    userInfo.email =
      account?.customer_details?.email || sbmUserDetail.customer.email
    userInfo.phone_number =
      account?.customer_details?.phone_number ||
      sbmUserDetail.customer.phone_number
    userAddress.customer_details.address = {
      street:
        account?.customer_details?.address?.street ||
        sbmUserDetail.customer.address?.street,
      number:
        account?.customer_details?.address?.number ||
        sbmUserDetail.customer.address?.number,
      postal_code:
        account?.customer_details?.address?.postal_code ||
        sbmUserDetail.customer.address?.postal_code,
      city:
        account?.customer_details?.address?.city ||
        sbmUserDetail.customer.address?.city,
      country: account?.country || sbmUserDetail.customer.address?.country,
      box:
        account?.customer_details?.address?.box ||
        sbmUserDetail.customer.address?.box
    }

    return { userInfo, userAddress }
  },
  addUnregistrableInstallation(_, installation) {
    const unregistrableInstallations =
      this.$storage.getUniversal('registration.unregistrable-installations') ||
      []
    // Check if the installation is already in the list
    const existingInstallation = unregistrableInstallations.find(
      (inst) => inst.localInstallationId === installation.localInstallationId
    )

    if (!existingInstallation) {
      const updatedInstallations = [...unregistrableInstallations, installation]
      this.$storage.setUniversal(
        'registration.unregistrable-installations',
        updatedInstallations
      )
    }
  }
}
